import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Alert, Table, Typography } from 'antd';

import { SOV_IMPORT_HEADERS, SOV_IMPORT_TYPE_LABELS } from './sovImportConstants';

const { Text } = Typography;

const NUMBER_KEYS = new Set([
  'contractAmount',
  'invoiceAmount',
  'percentageComplete',
  'estimatedQuantity',
  'billableQuantity',
  'completedQuantity',
  'quantityToDate',
]);

// Stored as a fraction, read as a percentage. Trailing zeroes are dropped: 0.45 -> 45%.
const toPercentageText = (fraction) => `${Number((fraction * 100).toFixed(2))}%`;

function renderImportCell(key, value, { hasSubItems, storedContractAmount }) {
  // A parent row may leave its amount blank, so show the sub-item total that will be stored.
  if (key === 'contractAmount' && value === null && hasSubItems) {
    return <Text type="secondary">{Number(storedContractAmount ?? 0).toFixed(2)}</Text>;
  }
  if (value === null || value === undefined || value === '') return '';
  if (key === 'percentageComplete') return toPercentageText(value);
  if (key === 'notesColumn') return <span style={{ whiteSpace: 'pre-line' }}>{value}</span>;
  return value;
}

const COLUMNS = [
  {
    title: 'Row',
    dataIndex: 'rowNumber',
    key: 'rowNumber',
    width: 64,
  },
  {
    title: 'Item',
    dataIndex: 'itemLabel',
    key: 'itemLabel',
    width: 64,
  },
  {
    title: 'Error',
    dataIndex: 'error',
    key: 'error',
    width: 320,
    render: (error) => (error ? <Text style={{ color: 'red', fontWeight: 'bold' }}>{error}</Text> : ''),
  },
  ...SOV_IMPORT_HEADERS.map(({ key, title }) => ({
    title,
    dataIndex: key,
    key,
    align: NUMBER_KEYS.has(key) ? 'right' : 'left',
    render: (value, record) => renderImportCell(key, value, record),
  })),
];

/**
 * Every row the backend read back from the sheet, read only, so the user can see and fix each
 * problem in one pass. Nothing is imported while a single row still has an error.
 */
export default function ScheduleOfValuesImportPreview({
  preview,
  loading,
}) {
  const dataSource = useMemo(() => (
    (preview?.rows ?? []).map((row, index) => {
      const messages = row.errors.map(({ message }) => message);

      return {
        ...row,
        // The Type cell shows what the sheet said when it could not be matched.
        type: row.type ? SOV_IMPORT_TYPE_LABELS[row.type] : row.typeText,
        // Columns show what the sheet stated. A parent that stated nothing falls back to the
        // total it will store, so a blank there does not read as a mistake.
        contractAmount: row.statedContractAmount,
        storedContractAmount: row.contractAmount,
        key: `${row.rowNumber ?? index}`,
        error: messages.join('; '),
      };
    })
  ), [preview]);

  const failedCount = preview?.errorCount ?? 0;

  return (
    <div>
      {!loading && !preview && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This spreadsheet could not be checked"
          description="Go back a step and try again."
        />
      )}
      {!loading && !!preview && (
        <Alert
          type={failedCount ? 'error' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message={`${dataSource.length} rows, ${failedCount} with errors`}
          description={failedCount
            ? 'Fix the highlighted rows in your spreadsheet and upload it again.'
            : undefined}
        />
      )}
      <Table
        columns={COLUMNS}
        dataSource={dataSource}
        loading={loading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="small"
        rowClassName={({ error }) => (error ? 'sov-import-error-row' : '')}
      />
    </div>
  );
}

ScheduleOfValuesImportPreview.propTypes = {
  preview: PropTypes.shape({
    errorCount: PropTypes.number,
    rows: PropTypes.arrayOf(PropTypes.shape({
      rowNumber: PropTypes.number,
      errors: PropTypes.arrayOf(PropTypes.shape({
        field: PropTypes.string,
        message: PropTypes.string,
      })),
    })),
  }),
  loading: PropTypes.bool,
};

ScheduleOfValuesImportPreview.defaultProps = {
  preview: null,
  loading: false,
};
